<script setup>
// A LISTA DE ANEXOS DE UM REGISTRO — nasceu para a obrigação (boleto,
// comprovante, nota, no modal e na página) e virou peça em 15/09/2026, quando
// a observação do financeiro do processo também passou a receber arquivos. Quem
// usa passa a API (`getAll`, `upload`, `delete` pelo id do registro), o título
// e a dica — o resto é o mesmo.
//
// Eram duas listas escritas à parte: linhas cinza com um clipe no modal, e na
// página um botão com `title=` (legenda nativa, que aparece tarde) e um
// formatador de tamanho próprio. Agora é a peça de documento do produto
// (`advup-doc`), com o formato colorido pelo programa que abre o arquivo.
//
// A lista carrega sozinha, e a falha tem nome: "nenhum arquivo" e "não
// consegui carregar" são frases opostas para quem procura um comprovante.
//
// `podeVer`: na página o nome de um PDF ou imagem abre o preview dentro da
// tela (o comprovante é feito para ser CONFERIDO); no modal abre em nova aba.
//
// SEM REGISTRO AINDA (06/10/2026, Dra. Jullya: "no financeiro tb, por exemplo
// juntar o RPV"): no formulário de criar, `registroId` vem vazio. Os arquivos
// escolhidos esperam na lista, e quem salvou chama `enviarPendentes(id)` com o
// registro que acabou de nascer.
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { fileFormatFor } from 'dashboard/helper/legalFileFormat';
import { formatBytes } from 'shared/helpers/FileHelper';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useConfirmacao } from 'dashboard/composables/useConfirmacao';

const props = defineProps({
  registroId: { type: [Number, String], default: null },
  // `{ getAll(id), upload(id, arquivo), delete(id, anexoId) }`.
  api: { type: Object, required: true },
  titulo: { type: String, default: '' },
  // O que anexar ali. Sem ela, a dica da obrigação (boleto, comprovante).
  dica: { type: String, default: '' },
  podeVer: { type: Boolean, default: false },
});

const emit = defineEmits(['ver']);

const { t } = useI18n();
// Remover um anexo já enviado apagava o arquivo no primeiro clique.
const { dialogo, textos, pedirConfirmacao, confirmar, confirmando } =
  useConfirmacao();
const { showAlert } = useAlert();

const anexos = ref([]);
const erro = ref(false);
const enviando = ref(false);
const removendo = ref(null);

const carregar = async () => {
  if (!props.registroId) {
    anexos.value = [];
    erro.value = false;
    return;
  }
  try {
    const { data } = await props.api.getAll(props.registroId);
    anexos.value = data.payload || [];
    erro.value = false;
  } catch {
    anexos.value = [];
    erro.value = true;
  }
};

watch(() => props.registroId, carregar, { immediate: true });

const formatoDo = anexo => fileFormatFor(anexo.filename);

const visualizavel = anexo =>
  /\.(pdf|png|jpe?g|gif|webp|bmp|svg)$/i.test(anexo?.filename || '');

const abreNaTela = anexo =>
  props.podeVer && !anexo.pendente && visualizavel(anexo);

// A PRÉVIA DA IMAGEM (Victor, 14/10/2026: "tem como ter a prévia da imagem
// anexada?"). O ladrilho do formato mostra a própria imagem; o endereço é o
// mesmo que o visor e o "abrir" usam. Imagem que não carrega volta ao ícone.
const miniaturaQueFalhou = ref(new Set());
const temMiniatura = anexo =>
  !anexo.pendente &&
  !!anexo.url &&
  !miniaturaQueFalhou.value.has(anexo.id) &&
  (String(anexo.content_type || '').startsWith('image/') ||
    /\.(png|jpe?g|gif|webp|bmp)$/i.test(anexo.filename || '')) &&
  !/svg/i.test(anexo.content_type || '');
const miniaturaFalhou = anexo => {
  miniaturaQueFalhou.value = new Set(miniaturaQueFalhou.value).add(anexo.id);
};
const abrirPelaMiniatura = anexo => {
  if (abreNaTela(anexo)) emit('ver', anexo);
};

let proximoPendente = 0;
const guardarPendentes = arquivos => {
  arquivos.forEach(arquivo => {
    proximoPendente += 1;
    anexos.value.push({
      id: `pendente-${proximoPendente}`,
      filename: arquivo.name,
      byte_size: arquivo.size,
      pendente: true,
      arquivo,
    });
  });
};

// Devolve os nomes que NÃO subiram: o registro já existe, quem chamou avisa.
async function enviarPendentes(registroId) {
  const pendentes = anexos.value.filter(a => a.pendente);
  const falharam = [];
  await Promise.all(
    pendentes.map(a =>
      props.api.upload(registroId, a.arquivo).catch(() => {
        falharam.push(a.filename);
      })
    )
  );
  return falharam;
}

defineExpose({ enviarPendentes });

async function enviar(evento) {
  if (!props.registroId) {
    guardarPendentes(Array.from(evento.target.files || []));
    evento.target.value = '';
    return;
  }
  const arquivo = evento.target.files[0];
  if (!arquivo) return;
  enviando.value = true;
  try {
    const { data } = await props.api.upload(props.registroId, arquivo);
    anexos.value.push(data.payload);
    showAlert(t('FINANCIAL_MODULE.SAVE_OK'));
  } catch {
    showAlert(t('FINANCIAL_MODULE.ATTACHMENT_ERROR'));
  } finally {
    enviando.value = false;
    evento.target.value = '';
  }
}

async function removerDeVerdade(anexo) {
  removendo.value = anexo.id;
  try {
    await props.api.delete(props.registroId, anexo.id);
    anexos.value = anexos.value.filter(item => item.id !== anexo.id);
  } catch {
    showAlert(t('FINANCIAL_MODULE.ATTACHMENT_ERROR'));
  } finally {
    removendo.value = null;
  }
}

function remover(anexo) {
  if (anexo.pendente) {
    anexos.value = anexos.value.filter(item => item.id !== anexo.id);
    return;
  }
  pedirConfirmacao(() => removerDeVerdade(anexo), {
    titulo: t('CONFIRMA_EXCLUSAO.ANEXO_TIT', {
      nome: anexo.nome || anexo.filename || '',
    }),
    descricao: t('CONFIRMA_EXCLUSAO.ANEXO_TXT'),
    rotulo: t('CONFIRMA_EXCLUSAO.ACAO'),
  });
}
</script>

<template>
  <section class="lista-anexos">
    <header class="lista-anexos__cab">
      <h3 class="advup-rotulo--secao lista-anexos__tit">
        {{ titulo || t('FINANCIAL_MODULE.ATTACHMENTS') }}
      </h3>
      <span v-if="anexos.length" class="lista-anexos__conta">
        {{ anexos.length }}
      </span>
      <label
        v-if="anexos.length"
        class="advup-btn advup-btn--vazado advup-btn--compacto lista-anexos__anexar"
      >
        <i
          :class="
            enviando ? 'i-ph-circle-notch advup-gira' : 'i-ph-upload-simple'
          "
          aria-hidden="true"
        />
        {{
          enviando
            ? t('FINANCIAL_MODULE.SAVING')
            : t('FINANCIAL_MODULE.ATTACH_FILE')
        }}
        <input
          type="file"
          class="sr-only"
          :multiple="!registroId"
          :disabled="enviando"
          @change="enviar"
        />
      </label>
    </header>

    <div v-if="erro" class="advup-vazio advup-vazio--erro">
      <span class="advup-vazio__icone" aria-hidden="true">
        <i class="i-ph-warning-circle" />
      </span>
      <p class="advup-vazio__txt">
        {{ t('FINANCIAL_MODULE.DETALHE.ATTACHMENTS_ERROR') }}
      </p>
      <button
        type="button"
        class="advup-btn advup-vazio__acao"
        @click="carregar"
      >
        {{ t('FINANCIAL_MODULE.DETALHE.RETRY') }}
      </button>
    </div>

    <!-- Sem anexo, o vazio é o próprio lugar de anexar: um "Nenhum arquivo"
         cinza ao lado de um botão pedia dois olhares para uma ação só. -->
    <label v-else-if="!anexos.length" class="advup-soltar">
      <i
        class="advup-soltar__ico"
        :class="enviando ? 'i-ph-circle-notch advup-gira' : 'i-ph-paperclip'"
        aria-hidden="true"
      />
      <span class="advup-soltar__tit">
        {{
          enviando
            ? t('FINANCIAL_MODULE.SAVING')
            : t('FINANCIAL_MODULE.ATTACHMENTS_EMPTY')
        }}
      </span>
      <span class="advup-soltar__dica">
        {{ dica || t('FINANCIAL_MODULE.DETALHE.ATTACH_HINT') }}
      </span>
      <input
        type="file"
        class="sr-only"
        :multiple="!registroId"
        :disabled="enviando"
        @change="enviar"
      />
    </label>

    <ul v-else class="advup-doc-lista">
      <li
        v-for="(anexo, i) in anexos"
        :key="anexo.id"
        class="advup-doc advup-entra fmt"
        :class="`fmt--${formatoDo(anexo).tone}`"
        :style="{ '--i': Math.min(i, 10) }"
      >
        <span
          v-tooltip.top="formatoDo(anexo).label"
          class="advup-doc__fmt"
          :class="{ 'lista-anexos__com-miniatura': temMiniatura(anexo) }"
          @click="abrirPelaMiniatura(anexo)"
        >
          <img
            v-if="temMiniatura(anexo)"
            :src="anexo.url"
            alt=""
            loading="lazy"
            class="lista-anexos__miniatura"
            @error="miniaturaFalhou(anexo)"
          />
          <template v-else>
            <i :class="formatoDo(anexo).icon" />
            <b v-if="formatoDo(anexo).label !== '—'" class="advup-doc__fmt-rot">
              {{ formatoDo(anexo).label }}
            </b>
          </template>
        </span>
        <div class="advup-doc__corpo">
          <div class="advup-doc__linha">
            <button
              v-if="abreNaTela(anexo)"
              v-tooltip.top="
                t('FINANCIAL_MODULE.ATTACHMENT_PREVIEW.CLICK_TO_VIEW')
              "
              type="button"
              class="advup-doc__tit lista-anexos__ver"
              @click="emit('ver', anexo)"
            >
              {{ anexo.filename }}
            </button>
            <span v-else-if="anexo.pendente" class="advup-doc__tit">
              {{ anexo.filename }}
            </span>
            <a
              v-else
              :href="anexo.url"
              target="_blank"
              rel="noopener noreferrer"
              class="advup-doc__tit"
            >
              {{ anexo.filename }}
            </a>
          </div>
          <p v-if="anexo.byte_size" class="advup-doc__meta">
            <span>{{ formatBytes(anexo.byte_size, 1) }}</span>
          </p>
        </div>
        <div class="advup-doc__acoes">
          <a
            v-if="!anexo.pendente"
            v-tooltip.top="t('FINANCIAL_MODULE.DETALHE.OPEN')"
            :href="anexo.url"
            target="_blank"
            rel="noopener noreferrer"
            class="advup-acao"
            :aria-label="`${t('FINANCIAL_MODULE.DETALHE.OPEN')}: ${anexo.filename}`"
          >
            <i class="i-ph-arrow-square-out" aria-hidden="true" />
          </a>
          <button
            v-tooltip.top="t('FINANCIAL_MODULE.ATTACHMENT_REMOVE')"
            type="button"
            class="advup-acao advup-acao--perigo"
            :disabled="removendo === anexo.id"
            :aria-label="`${t('FINANCIAL_MODULE.ATTACHMENT_REMOVE')}: ${anexo.filename}`"
            @click="remover(anexo)"
          >
            <i class="i-ph-trash" aria-hidden="true" />
          </button>
        </div>
      </li>
    </ul>
    <Dialog
      ref="dialogo"
      type="alert"
      :title="textos.titulo"
      :description="textos.descricao"
      :confirm-button-label="textos.rotulo"
      :is-loading="confirmando"
      @confirm="confirmar"
    />
  </section>
</template>

<style scoped>
.lista-anexos {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
}

.lista-anexos__cab {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  min-height: 32px;
}

.lista-anexos__tit {
  margin: 0;
  color: rgb(var(--slate-11));
}

.lista-anexos__conta {
  display: inline-grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 var(--esp-1);
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-3));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  line-height: 1;
  color: rgb(var(--slate-12));
  font-variant-numeric: tabular-nums;
}

.lista-anexos__anexar {
  margin-left: auto;
  cursor: pointer;
}

/* O nome que abre o preview é botão, com a mesma letra do título da peça. */
.lista-anexos__ver {
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

/* O anel só com teclado: com o foco entrando por clique, a área de soltar
   ficava contornada à toa. */
/* A imagem precisa de mais lugar que um ícone para servir de prévia: com os
   46px do ladrilho, um print virava uma faixa ilegível (14/10/2026). */
.lista-anexos__com-miniatura {
  width: 72px;
  height: 72px;
  cursor: zoom-in;
}

.lista-anexos__miniatura {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
